import React, { useState } from 'react';
    import { motion } from 'framer-motion';
    import { useAuth } from '../context/AuthContext';
    import { useData } from '../context/DataContext';
    import ForgotPasswordModal from './ForgotPasswordModal';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';
    import firebaseInitService from '../services/firebaseInitService';

    const { FiUser, FiLock, FiLogIn, FiEye, FiEyeOff, FiShield, FiAlertCircle, FiRefreshCw } = FiIcons;

    const Login = () => {
      const [email, setEmail] = useState('');
      const [password, setPassword] = useState('');
      const [showPassword, setShowPassword] = useState(false);
      const [error, setError] = useState('');
      const [loading, setLoading] = useState(false);
      const [fixing, setFixing] = useState(false);
      const [showForgotPassword, setShowForgotPassword] = useState(false);
      const { login } = useAuth();
      const { appSettings, dataLoaded } = useData();

      const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
          // Convert username shortcut to full email for admin
          let loginEmail = email;
          if (email.toLowerCase() === 'admin') {
            loginEmail = 'admin@expertguru.com';
          }
          const result = await login(loginEmail, password);
          if (!result.success) {
            setError(result.message);
          }
        } catch (error) {
          console.error('Login: Form error:', error);
          setError('An unexpected error occurred. Please try again.');
        } finally {
          setLoading(false);
        }
      };

      const handleForgotPassword = (e) => {
        e.preventDefault();
        setShowForgotPassword(true);
      };

      const handleEmergencyFix = async () => {
        setFixing(true);
        setError('');
        try {
          await firebaseInitService.emergencyInitialization();
          alert('Database initialized! Please try logging in again.');
          window.location.reload();
        } catch (err) {
          alert('Fix failed: ' + err.message);
        } finally {
          setFixing(false);
        }
      };

      if (!dataLoaded) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Loading ExpertGuru...</p>
            </div>
          </div>
        );
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-md w-full">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="text-center mb-8">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.2, type: "spring", stiffness: 200 }} className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: appSettings.brandColor }}>
                  {appSettings.logoUrl ? (
                    <img src={appSettings.logoUrl} alt={appSettings.companyName} className="w-10 h-10 object-contain" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                  ) : null}
                  <SafeIcon icon={FiShield} className="text-white text-2xl" style={{ display: appSettings.logoUrl ? 'none' : 'block' }} />
                </motion.div>
                <h1 className="text-2xl font-bold text-gray-900">{appSettings.companyName}</h1>
                <p className="text-gray-600 mt-2">Welcome back! Please sign in to continue.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email / Username</label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all" placeholder="Enter your email or username" required disabled={loading} />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Password</label>
                    <a href="#" onClick={handleForgotPassword} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">Forgot password?</a>
                  </div>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all" placeholder="Enter your password" required disabled={loading} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50" disabled={loading}>
                      <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                    </button>
                  </div>
                </div>

                {error && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <div className="flex items-start gap-2">
                      <SafeIcon icon={FiAlertCircle} className="mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium">Login Failed</p>
                        <p className="text-sm">{error}</p>
                        {(error.includes('data') || error.includes('found') || error.includes('Init') || email.includes('admin')) && (
                          <button type="button" onClick={handleEmergencyFix} disabled={fixing} className="mt-2 flex items-center gap-1 text-xs bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded transition-colors font-medium">
                            {fixing ? <div className="w-3 h-3 border-2 border-red-800 border-t-transparent rounded-full animate-spin"></div> : <SafeIcon icon={FiRefreshCw} />}
                            {fixing ? 'Fixing...' : 'Run Emergency Database Fix'}
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                <motion.button whileHover={{ scale: loading ? 1 : 1.02 }} whileTap={{ scale: loading ? 1 : 0.98 }} type="submit" disabled={loading || fixing} className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" style={{ backgroundColor: loading ? '#9CA3AF' : appSettings.brandColor }}>
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiLogIn} /> Sign In
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </motion.div>
          {showForgotPassword && <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />}
        </div>
      );
    };

    export default Login;